import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CustomSelect from '../../Components/customs/dropdown';
import {
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  History,
} from 'lucide-react';

const BRAND = '#375AE7';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const QUICK_FILTERS = [
  {
    value: 'last-month',
    label: 'Last month',
    icon: History,
  },
  {
    value: 'last-3-months',
    label: 'Last 3 months',
    icon: CalendarRange,
  },
  {
    value: 'all-time',
    label: 'All time',
    icon: Clock3,
  },
];

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

const getMonthValue = (spendPeriod) => ({
  type: 'month',
  year: Number(spendPeriod?.year) || currentYear,
  month: Number(spendPeriod?.month) || currentMonth,
});

const monthLabel = (year, month) =>
  `${MONTHS[Math.max(0, Number(month) - 1)]} ${year}`;

function isSameFilter(left, right) {
  if (!left || !right || left.type !== right.type) return false;
  if (left.type !== 'month') return true;
  return left.year === right.year && left.month === right.month;
}

export default function SpendCalendarFilter({
  spendPeriod,
  spend,
  value,
  onChange,
  shiftSpendMonth,
  minYear = currentYear - 10,
  maxYear = currentYear,
}) {
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const initialFilter = value || getMonthValue(spendPeriod);
  const [filter, setFilter] = useState(initialFilter);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, right: 16 });
  const [pickerYear, setPickerYear] = useState(
    initialFilter.type === 'month' ? initialFilter.year : currentYear,
  );

  useEffect(() => {
    if (value && !isSameFilter(value, filter)) {
      setFilter(value);
      if (value.type === 'month') setPickerYear(value.year);
    }
  }, [value]);

  /* Keep the local month display in step with the parent's spendPeriod
     when it changes (uncontrolled mode). Non-month selections (quick
     filters) are left alone so they aren't stomped. */
  useEffect(() => {
    if (value || filter.type !== 'month') return;
    const pv = getMonthValue(spendPeriod);
    if (filter.year !== pv.year || filter.month !== pv.month) {
      setFilter({ type: 'month', year: pv.year, month: pv.month });
      setPickerYear(pv.year);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spendPeriod]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideTrigger = containerRef.current?.contains(event.target);
      const clickedInsidePopover = popoverRef.current?.contains(event.target);

      if (!clickedInsideTrigger && !clickedInsidePopover) {
        setPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const years = useMemo(() => {
    const start = Math.min(Number(minYear) || currentYear - 10, currentYear);
    const end = Math.max(Number(maxYear) || currentYear, start);
    return Array.from({ length: end - start + 1 }, (_, index) => end - index);
  }, [maxYear, minYear]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const updatePopoverPosition = () => {
      const trigger = containerRef.current?.getBoundingClientRect();
      if (!trigger) return;

      const menuHeight = popoverRef.current?.offsetHeight || 320;
      const gap = 8;
      const viewportPadding = 12;
      const spaceBelow = window.innerHeight - trigger.bottom;
      const shouldOpenAbove = spaceBelow < menuHeight + gap + viewportPadding;
      const maxTop = Math.max(
        viewportPadding,
        window.innerHeight - menuHeight - viewportPadding,
      );
      const top = shouldOpenAbove
        ? Math.max(viewportPadding, trigger.top - menuHeight - gap)
        : Math.min(trigger.bottom + gap, maxTop);

      setPopoverPosition({
        top,
        right: Math.max(viewportPadding, window.innerWidth - trigger.right),
      });
    };

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [pickerOpen]);

  const togglePicker = () => {
    if (pickerOpen) {
      setPickerOpen(false);
      return;
    }

    if (activeMonth) setPickerYear(activeMonth.year);
    setPickerOpen(true);
  };

  const activeMonth = filter.type === 'month' ? filter : null;
  const displayLabel = activeMonth
    ? monthLabel(activeMonth.year, activeMonth.month)
    : QUICK_FILTERS.find((option) => option.value === filter.type)?.label ||
      'Select period';

  /* Push a chosen month back to the parent.
     · Controlled mode (onChange provided) — hand the filter over as-is.
     · Uncontrolled mode — the parent only exposes shiftSpendMonth(delta),
       so compute how many months the selection is away from the CURRENT
       spendPeriod and shift by that. Before this fix, picking a month in
       the popover (month grid / year dropdown) only updated the local
       label — spendPeriod and the API query never changed. */
  const syncWithParent = (nextFilter) => {
    if (onChange) {
      onChange(nextFilter);
      return;
    }
    if (nextFilter?.type !== 'month') return;
    const base = getMonthValue(spendPeriod);
    const delta =
      (nextFilter.year - base.year) * 12 + (nextFilter.month - base.month);
    if (delta !== 0) shiftSpendMonth?.(delta);
  };

  const emitFilter = (nextFilter) => {
    setFilter(nextFilter);
    syncWithParent(nextFilter);
  };

  const selectQuickFilter = (type) => {
    /* The parent's API filter is month-granular (year + month). In
       uncontrolled mode, resolve the "Last month" preset to a concrete
       month so it actually moves spendPeriod; the other presets have no
       month equivalent and remain display-only. */
    if (!onChange && type === 'last-month') {
      const base = getMonthValue(spendPeriod);
      const date = new Date(base.year, base.month - 2, 1);
      const monthFilter = {
        type: 'month',
        year: date.getFullYear(),
        month: date.getMonth() + 1,
      };
      setFilter(monthFilter);
      setPickerYear(monthFilter.year);
      syncWithParent(monthFilter);
      setPickerOpen(false);
      return;
    }
    emitFilter({ type });
    setPickerOpen(false);
  };

  const selectMonth = (month) => {
    emitFilter({
      type: 'month',
      year: pickerYear,
      month,
    });
    setPickerOpen(false);
  };

  const moveMonth = (direction) => {
    const base = activeMonth || getMonthValue(spendPeriod);
    const date = new Date(base.year, base.month - 1 + direction, 1);
    const nextFilter = {
      type: 'month',
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    };

    setPickerYear(nextFilter.year);
    setFilter(nextFilter);
    syncWithParent(nextFilter);
  };

  return (
    <div
      ref={containerRef}
      className="relative flex w-full items-center justify-end sm:w-auto"
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => moveMonth(-1)}
          className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-[#375AE7]/30 hover:bg-[#EEF2FF] hover:text-[#375AE7]"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={togglePicker}
          className="group inline-flex h-9 min-w-[148px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 shadow-sm transition hover:border-[#375AE7]/35 hover:shadow-md"
          aria-expanded={pickerOpen}
          aria-haspopup="dialog"
        >
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-[#EEF2FF] text-[#375AE7]">
            <CalendarDays className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0 flex-1 truncate text-left text-xs font-bold text-slate-800">
            {displayLabel}
          </span>
          {activeMonth ? (
            <span
              title={spend?.period_open ? 'Accruing' : 'Closed period'}
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                spend?.period_open
                  ? 'animate-pulse bg-blue-500'
                  : 'bg-slate-300'
              }`}
            />
          ) : null}
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${
              pickerOpen ? 'rotate-180 text-[#375AE7]' : ''
            }`}
          />
        </button>

        <button
          type="button"
          onClick={() => moveMonth(1)}
          className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-[#375AE7]/30 hover:bg-[#EEF2FF] hover:text-[#375AE7]"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {pickerOpen
        ? createPortal(
            <div
              ref={popoverRef}
              style={{ top: popoverPosition.top, right: popoverPosition.right }}
              className="fixed z-[100] max-h-[calc(100vh-1.5rem)] w-[min(320px,calc(100vw-2rem))] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2.5 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.4)]"
            >
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-xs font-bold text-slate-800">
                  Time period
                </span>
                <span className="text-[10px] font-semibold text-slate-400">
                  {activeMonth
                    ? monthLabel(activeMonth.year, activeMonth.month)
                    : displayLabel}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
                {QUICK_FILTERS.map(
                  ({ value: optionValue, label: optionLabel, icon: Icon }) => {
                    const selected = filter.type === optionValue;
                    return (
                      <button
                        type="button"
                        key={optionValue}
                        onClick={() => selectQuickFilter(optionValue)}
                        className={`inline-flex items-center justify-center gap-1 rounded-lg px-1.5 py-2 text-[10px] font-bold transition sm:text-[11px] ${
                          selected
                            ? 'bg-white text-[#375AE7] shadow-sm'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="whitespace-nowrap">{optionLabel}</span>
                      </button>
                    );
                  },
                )}
              </div>

              <div className="my-2.5 h-px bg-slate-100" />

              <div className="flex items-center justify-between px-1">
                <span className="text-xs font-bold text-slate-700">Month</span>
                <CustomSelect
                  value={pickerYear}
                  options={years.map((year) => ({
                    value: year,
                    label: String(year),
                  }))}
                  onChange={(year) => setPickerYear(Number(year))}
                  leadingIcon={CalendarDays}
                  ariaLabel="Select year"
                  buttonClassName="h-8 rounded-lg px-2.5"
                  menuClassName="min-w-[92px]"
                />
              </div>

              <div className="mt-2 grid grid-cols-4 gap-1">
                {MONTHS.map((month, index) => {
                  const monthNumber = index + 1;
                  const selected =
                    activeMonth?.year === pickerYear &&
                    activeMonth?.month === monthNumber;
                  return (
                    <button
                      type="button"
                      key={month}
                      onClick={() => selectMonth(monthNumber)}
                      className={`relative rounded-lg px-2 py-2 text-[11px] font-semibold transition ${
                        selected
                          ? 'bg-[#375AE7] text-white shadow-sm'
                          : 'text-slate-600 hover:bg-[#EEF2FF] hover:text-[#375AE7]'
                      }`}
                    >
                      {month}
                      {selected ? (
                        <Check
                          className="absolute right-1 top-1 h-2.5 w-2.5"
                          strokeWidth={3}
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
